import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'
import { useProductStore } from "@/js/stores/productStore"
import { getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import { formatFxtSKU } from './fxt-utils'

export function getAccessoryQuantity(rogue: IRogue, mainBuyRequest: BuyRequest, accessoryName: string): number | undefined {
  return mainBuyRequest.children?.filter(child => child.qty > 0 && rogue.getProductById(child.productId).name?.toLowerCase().includes(accessoryName.toLowerCase()))?.[0]?.qty
}

export function findBundleProduct(rogue: IRogue, mainBuyRequest: BuyRequest, bundleTitle: string, onlyIfSelected = false): Product | undefined {
  try {
    // Find the bundle buy request itself.
    // The main buy request may be a group - where the first child is the bundle, and the rest are accessories
    let bundleBuyRequest = mainBuyRequest
    let bundleProduct = rogue.getProductById(bundleBuyRequest?.productId)

    while (bundleProduct && bundleProduct.type !== 'bundle' && bundleBuyRequest.children) {
      bundleBuyRequest = bundleBuyRequest.children[0]
      bundleProduct = rogue.getProductById(bundleBuyRequest?.productId)
    }

    if (!bundleProduct) {
      return undefined
    }

    // Find the optionId on the bundle product that we are looking to reference
    const bundleOptId = bundleProduct.bundleOptions?.find(opt => opt.title.toLowerCase().includes(bundleTitle.toLowerCase()))?.id

    // Find the customer's selection from their active RHPA
    const buyRequest = bundleBuyRequest.children?.find(child => child.metadata?.bundleOptionId === bundleOptId)

    if (buyRequest && bundleOptId && (!onlyIfSelected || buyRequest.qty > 0)) {
      return getSimpleForBuyRequest(buyRequest, rogue)
    }

    return undefined
  } catch (e) {
    rogue.log.warn('Error finding bundle product', e as Error)

    return undefined
  }
}

export function findCustomOption(rogue: IRogue, buyRequest: BuyRequest, optionName: string) {
  let product = rogue.getProductById(buyRequest.productId)
  let targetBuyRequest = buyRequest

  // Change the product and BR to the child that has customOptions if necessary
  if (product.customOptions?.length === 0 && buyRequest.children) {
    product = rogue.getProductById(buyRequest.children[0]?.productId)
    targetBuyRequest = buyRequest.children[0]
  }

  if (product?.customOptions?.length === 0 && targetBuyRequest.children?.[0]) {
    product = rogue.getProductById(targetBuyRequest.children[0].productId)
    targetBuyRequest = targetBuyRequest.children![0]
  }

  if (!product || !targetBuyRequest || !product.customOptions?.length) {
    return undefined
  }

  // Find the optionId for the optionaName that we are looking to reference
  const optionId = Number(product.customOptions
    ?.find(option => option.title.toLocaleLowerCase().includes(optionName.toLowerCase()))?.id)

  const selectedValueID = targetBuyRequest.options?.[optionId]

  // Retrieve the set of options associated with the specified category
  const categoryOptions = product.customOptions?.find(option => option.id === optionId)

  // Find the customer's selection and return the title
  return categoryOptions?.values?.find(value => value.id === selectedValueID)?.title
}

export async function getGroupedParentId(product: Product, rogue: IRogue) {
  const productStore = useProductStore()
  const isFXTSku = formatFxtSKU(product.name, product.sku)

  // We can't find the group parent on FXT's like we do on other products
  // so we have to match the SKU with the product name then load it via the product store
  if (isFXTSku) {
    const fxtProduct = await productStore.load(isFXTSku, rogue)

    return fxtProduct?.productId

  } else {
    // Otherwise loop through product types until we have the grouped product
    try {
      let groupedProduct = product

      while (groupedProduct.type !== 'grouped') {
        groupedProduct = await productStore.findParent(groupedProduct.productId, rogue)
      }

      if (!groupedProduct) {
        return undefined
      }

      return groupedProduct.productId
    } catch (e) {
      rogue.log.warn('Error finding grouped product', e as Error)

      return undefined
    }
  }
}
